import { useContext } from "react"
import PropTypes from "prop-types"
import { Steps } from "primereact/steps"
import gaTracking from "../../helpers/gaTracking"
import { confirmPopup } from "primereact/confirmpopup"

// Custom components
import { LoaderProgressBar, LoaderSmall } from "../../components/Loaders"
import { UserContext } from "../../context/userContext"
import FormErrorPanel from "./components/FormErrorPanel"

// Styles
import styles from "../../styles/Form.module.css"

export default function FormSubmitting(props) {
  const userCtx = useContext(UserContext)

  const {
    activeStep,
    error,
    committedBeforeError,
    message,
    transactionId,
    idStamp = "",
    templateData,
    formId,
    onGoBack,
    resubmitCount,
    resubmit,
  } = props

  const steps = [
    { label: "Validation" },
    { label: "Form Submission" },
    { label: "Confirmation" },
  ]

  const handleSteps = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <h2>Validation Step</h2>
            <div>Please don't refresh your browser.</div>
          </>
        )
      case 1:
        return (
          <>
            <h2>Form Submission Step</h2>
            <div>
              Transaction ID:{" "}
              <code className={styles.errorCode}>{transactionId}</code>
            </div>
          </>
        )
      case 2: {
        /*Google Analytics tracking*/
        const gaTrackingParams = {
          event: "template_finish",
          userCtx,
          payload: {
            template_name: templateData.template.template_name,
            template_category: templateData.template.template_category,
            stamp_id: idStamp,
            transaction_id: transactionId,
            form_id: formId,
          },
        }
        gaTracking(gaTrackingParams)
        /*End Google Analytics tracking*/

        return (
          <>
            <h2 className={styles.successHeader}>
              <span className="material-icons">check_circle</span>Completed
              successfully.
            </h2>
            {message && <h3>{message}</h3>}
            <div className={styles.successMsg}>
              You will be redirected automatically.
            </div>

            <LoaderSmall />
          </>
        )
      }
      default:
        return null
    }
  }

  const handleError = () => {
    if (!error) return null

    /*Google Analytics tracking*/
    const gaTrackingParams = {
      event: "exception",
      userCtx,
      payload: {
        template_name: templateData.template.template_name,
        template_category: templateData.template.template_category,
        transaction_id: transactionId,
        stamp_id: idStamp,
        form_id: formId,
        error: message,
      },
    }
    gaTracking(gaTrackingParams)
    /*End Google Analytics tracking*/

    return (
      <FormErrorPanel
        title={
          committedBeforeError
            ? "Error in post-processing."
            : "Error processing data."
        }
        subHeader={
          <h3>
            <span className={styles.errorContainerSubHeader}>
              {committedBeforeError
                ? "Form was submitted but other errors occurred."
                : "Form submission was not made,"}
            </span>
            {!committedBeforeError ? " but customer support can help!" : ""}
          </h3>
        }
        transactionId={transactionId}
        messageText={message}
        showSupport
        onGoBack={cancelConfirmation}
        resubmitCount={resubmitCount}
        resubmit={resubmit}
      />
    )
  }

  const cancelConfirmation = (event) => {
    confirmPopup({
      target: event.currentTarget,
      message: (
        <>
          All entered data will be lost.
          <br />
          Are you sure you want to proceed?
        </>
      ),
      icon: "pi pi-exclamation-triangle",
      defaultFocus: "accept",
      accept() {
        onGoBack()
      },
    })
  }

  const displayLoader = !error && activeStep < 2

  return (
    <div>
      <div className={styles.stepsWrapper}>
        {displayLoader && <LoaderProgressBar />}
        <Steps
          model={steps}
          activeIndex={activeStep}
          readOnly={true}
          className={styles.stepsComponent}
        />
      </div>

      <div
        className={`${styles.stepsContent} ${error ? styles.stepsContentErrorSpacing : ""}`}
      >
        {handleSteps()}
      </div>
      {handleError()}
    </div>
  )
}

FormSubmitting.propTypes = {
  activeStep: PropTypes.number.isRequired,
  error: PropTypes.bool.isRequired,
  committedBeforeError: PropTypes.bool,
  message: PropTypes.string.isRequired,
  idStamp: PropTypes.string.isRequired,
  formId: PropTypes.string.isRequired,
  templateData: PropTypes.object.isRequired,
  transactionId: PropTypes.string.isRequired,
  onGoBack: PropTypes.func.isRequired,
  resubmitCount: PropTypes.number.isRequired,
  resubmit: PropTypes.func.isRequired,
}
